import { emptyCardIntent } from '../intent.js'
import type { BoardSnapshot } from '../schemas.js'
import { mulberry32, seededId } from './random.js'

const CREATED = '2026-01-05T09:00:00.000Z'

interface CardSeed {
  title: string
  status: 'new' | 'planned' | 'in_progress' | 'blocked' | 'done'
  owner?: string
  colour?: 'default' | 'blue' | 'green' | 'orange' | 'red'
  estimateMinutes?: number | null
  release?: string
  tags?: string[]
  description?: string
}

/** The demo board shown on first load: small, readable, and exercising every field. */
export function exampleBoard(): BoardSnapshot {
  const rng = mulberry32(20260105)
  const boardId = seededId(rng)

  const groupSeeds = ['Discover', 'Deliver']
  const groups = groupSeeds.map((title) => ({ id: seededId(rng), boardId, title }))

  const columnSeeds: Array<[string, number | null]> = [
    ['Find a plan', 0],
    ['Compare options', 0],
    ['Sign up', 1],
    ['Set up workspace', 1],
    ['Invite the team', 1],
  ]
  const columns = columnSeeds.map(([title, groupIndex], position) => ({
    id: seededId(rng),
    boardId,
    title,
    groupId: groupIndex === null ? null : (groups[groupIndex]?.id ?? null),
    position,
  }))

  const swimlaneSeeds = ['Release 1', 'Later']
  const swimlanes = swimlaneSeeds.map((title, position) => ({ id: seededId(rng), boardId, title, position }))

  const rowSeeds: Array<[number, string]> = [
    [0, 'Must have'],
    [0, 'Should have'],
    [1, 'Ideas'],
  ]
  const rows = rowSeeds.map(([swimlaneIndex, title], index) => ({
    id: seededId(rng),
    boardId,
    swimlaneId: swimlanes[swimlaneIndex]!.id,
    title,
    position: rowSeeds.slice(0, index).filter(([s]) => s === swimlaneIndex).length,
  }))

  const plan: Array<[number, number, CardSeed[]]> = [
    [0, 0, [
      { title: 'Pricing page lists every plan', status: 'done', owner: 'Ana', colour: 'green', estimateMinutes: 120, release: 'R1', tags: ['ui'] },
      { title: 'Plan comparison is readable on mobile', status: 'in_progress', owner: 'Bo', estimateMinutes: 90, release: 'R1', tags: ['ui', 'a11y'] },
    ]],
    [0, 1, [
      { title: 'Highlight the recommended plan', status: 'planned', owner: 'Ana', colour: 'blue', estimateMinutes: 60, release: 'R1' },
    ]],
    [0, 2, [
      { title: 'Email and password sign-up', status: 'in_progress', owner: 'Chidi', colour: 'blue', estimateMinutes: 240, release: 'R1', tags: ['api'], description: 'Includes validation, duplicate-address handling and a confirmation email.' },
      { title: 'Sign up with a work account', status: 'blocked', owner: 'Chidi', colour: 'red', estimateMinutes: 480, release: 'R2', tags: ['api', 'risk'], description: 'Waiting on the identity provider sandbox.' },
    ]],
    [0, 3, [
      { title: 'Create the first workspace', status: 'planned', owner: 'Dara', estimateMinutes: 180, release: 'R1' },
    ]],
    [1, 0, [
      { title: 'Show a worked example per plan', status: 'new', colour: 'orange', estimateMinutes: 45, tags: ['research'] },
    ]],
    [1, 2, [
      { title: 'Remember an interrupted sign-up', status: 'new', estimateMinutes: null, tags: ['spike'] },
    ]],
    [1, 4, [
      { title: 'Invite by shared link', status: 'planned', owner: 'Eli', estimateMinutes: 150, release: 'R2' },
      { title: 'Bulk invite from a pasted list', status: 'new', estimateMinutes: 300, release: 'R3', tags: ['ui'] },
    ]],
    [2, 1, [
      { title: 'Annual billing toggle', status: 'new', colour: 'orange', estimateMinutes: 240, release: 'Backlog', tags: ['debt'] },
    ]],
    [2, 4, [
      { title: 'Suggest teammates from the domain', status: 'new', estimateMinutes: null, release: 'Backlog', tags: ['research', 'risk'] },
    ]],
  ]

  const cards = plan.flatMap(([rowIndex, columnIndex, seeds]) =>
    seeds.map((seed, position) => ({
      id: seededId(rng),
      boardId,
      rowId: rows[rowIndex]!.id,
      columnId: columns[columnIndex]!.id,
      position,
      title: seed.title,
      description: seed.description ?? '',
      type: null,
      status: seed.status,
      priority: null,
      moscow: null,
      owner: seed.owner ?? '',
      tags: seed.tags ?? [],
      colour: seed.colour ?? ('default' as const),
      icon: null,
      estimateMinutes: seed.estimateMinutes ?? null,
      dueDate: null,
      release: seed.release ?? '',
      intent: emptyCardIntent(),
      createdAt: CREATED,
      updatedAt: CREATED,
    })),
  )

  return {
    board: { id: boardId, name: 'Sign-up journey', revision: 0, createdAt: CREATED, updatedAt: CREATED },
    columnGroups: groups,
    columns,
    swimlanes,
    rows,
    cards,
  }
}
